import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tag, X, Check, Plus, FloppyDisk } from '@phosphor-icons/react';
import { getTagColor } from '../lib/tagColor';

// Inline tag editor with autocomplete from the project's existing tags.
// Click to edit → shows current tags as removable chips plus an input that
// suggests tags already used anywhere on the board (so users reuse them
// instead of retyping / creating duplicates). Enter or comma adds; a new
// value not in the list can be created on the fly.
export default function TagsField({ projectId, value = [], canEdit, onSave, testid = 'tags-field' }) {
  const { API, getOrgHeaders } = useAuth();
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState(value);
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { setTags(value); }, [value]);

  const h = () => ({ withCredentials: true, headers: getOrgHeaders() });

  const openEdit = async () => {
    if (!canEdit) return;
    setTags(value);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/tags`, h());
      setSuggestions(Array.isArray(data) ? data : []);
    } catch { setSuggestions([]); }
  };

  const addTag = (t) => {
    const v = String(t || '').trim();
    if (!v || tags.includes(v)) { setQ(''); return; }
    setTags(prev => [...prev, v]);
    setQ('');
  };
  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));
  const commit = () => {
    const changed = tags.length !== value.length || tags.some((t, i) => t !== value[i]);
    if (changed) onSave(tags);
    setEditing(false);
  };
  const cancel = () => { setTags(value); setQ(''); setEditing(false); };

  const query = q.trim().toLowerCase();
  const available = suggestions.filter(s => !tags.includes(s) && (!query || s.toLowerCase().includes(query)));
  const canCreate = q.trim() && !suggestions.some(s => s.toLowerCase() === q.trim().toLowerCase()) && !tags.some(s => s.toLowerCase() === q.trim().toLowerCase());

  if (!editing) {
    return (
      <div
        onClick={openEdit}
        data-testid={`${testid}-display`}
        className={`text-[13px] rounded-md px-2 py-1 -mx-2 min-h-[28px] flex flex-wrap gap-1 items-center transition-all ${canEdit ? 'cursor-pointer hover:bg-zinc-100/80 hover:ring-1 hover:ring-zinc-200' : ''}`}
        title={canEdit ? 'Click to edit tags' : undefined}
      >
        {tags.length > 0 ? tags.map(t => {
          const c = getTagColor(t);
          return (
            <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${c.bg} ${c.text} ${c.ring}`}>
              <Tag size={10} weight="fill" className="opacity-70" />{t}
            </span>
          );
        }) : <span className="text-zinc-400 italic text-[13px]">Click to set</span>}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-300 ring-2 ring-blue-100 bg-white p-2.5 space-y-2" data-testid={`${testid}-editor`}>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(t => {
            const c = getTagColor(t);
            return (
              <span key={t} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-medium ring-1 ${c.bg} ${c.text} ${c.ring}`}>
                {t}
                <button type="button" onClick={() => removeTag(t)} className="opacity-60 hover:opacity-100" data-testid={`${testid}-remove-${t}`}><X size={11} weight="bold" /></button>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative">
        <Tag size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          ref={inputRef}
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); if (q.trim()) addTag(q); }
            else if (e.key === 'Backspace' && !q && tags.length) removeTag(tags[tags.length - 1]);
            else if (e.key === 'Escape') cancel();
          }}
          placeholder="Search or add a tag…"
          className="w-full h-8 pl-7 pr-2 text-[12px] rounded-md border border-zinc-200 focus:outline-none focus:border-blue-400"
          data-testid={`${testid}-input`}
        />
      </div>
      {(available.length > 0 || canCreate) && (
        <div className="max-h-36 overflow-y-auto rounded-md border border-zinc-100 bg-white -mx-0.5">
          {canCreate && (
            <button type="button" onClick={() => addTag(q)} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-blue-600 hover:bg-blue-50 text-left" data-testid={`${testid}-create`}>
              <Plus size={12} weight="bold" /> Create “{q.trim()}”
            </button>
          )}
          {available.map(s => {
            const c = getTagColor(s);
            return (
              <button key={s} type="button" onClick={() => addTag(s)} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] hover:bg-zinc-50 text-left" data-testid={`${testid}-suggestion-${s}`}>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10.5px] font-medium ring-1 ${c.bg} ${c.text} ${c.ring}`}><Tag size={9} weight="fill" />{s}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-zinc-100">
        <Button variant="ghost" size="sm" onClick={cancel} className="h-7 px-2 text-[11px] text-zinc-500 hover:text-zinc-700"><X size={12} className="mr-0.5" /> Cancel</Button>
        <Button size="sm" onClick={commit} className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white text-[11px]" data-testid={`${testid}-save`}><FloppyDisk size={12} className="mr-1" /> Save</Button>
      </div>
    </div>
  );
}
